import { Splide } from '@splidejs/splide';
import '@splidejs/splide/css';
import { windowWidth } from './utils';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

const sliders = {
  init() {
    this.initImmediately('.jsHeroSlider', this.heroSlider.bind(this));
    this.initImmediately('#mainImage', this.setProdThumbnailsSlider.bind(this));
    this.initImmediately(
      '#thumbnails',
      this.setProdThumbnailsSlider.bind(this),
    );
    this.initOnScroll(
      '#mainImageGallery',
      this.setProdGallerySlider.bind(this),
    );
    this.initOnScroll(
      '#thumbnailsGallery',
      this.setProdGallerySlider.bind(this),
    );
    this.initImmediately('.jsTextSlider', this.textSlider.bind(this));
    this.initOnScroll('.jsProdSlider', this.prodSlider.bind(this));
    this.initOnScroll('.jsCatsSlider', this.catsSlider.bind(this));
    this.initOnScroll('.jsItemSlider', this.itemSlider.bind(this));
    this.resetSlider();
  },

  resetSlider() {
    window.addEventListener('reset', () => {
      this.initImmediately(
        '#mainImage',
        this.setProdThumbnailsSlider.bind(this),
      );
      this.initImmediately(
        '#thumbnails',
        this.setProdThumbnailsSlider.bind(this),
      );
      this.initOnScroll(
        '#mainImageGallery',
        this.setProdGallerySlider.bind(this),
      );
      this.initOnScroll(
        '#thumbnailsGallery',
        this.setProdGallerySlider.bind(this),
      );
    });
  },

  initImmediately(selector: string, callback: (el: HTMLElement) => void) {
    const elements = document.querySelectorAll(
      selector,
    ) as NodeListOf<HTMLElement>;

    elements.forEach((el) => {
      if (el.dataset.sliderInitialized === 'true') return;

      el.dataset.sliderInitialized = 'true';
      callback(el);
    });
  },

  initOnScroll(selector: string, callback: (el: HTMLElement) => void) {
    const elements = document.querySelectorAll(
      selector,
    ) as NodeListOf<HTMLElement>;

    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => {
        if (el.dataset.sliderInitialized === 'true') return;

        el.dataset.sliderInitialized = 'true';
        callback(el);
      });

      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target as HTMLElement;

          if (el.dataset.sliderInitialized === 'true') {
            obs.unobserve(el);
            return;
          }

          el.dataset.sliderInitialized = 'true';
          obs.unobserve(el);

          callback(el);
        });
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0,
      },
    );

    elements.forEach((el) => {
      if (el.dataset.sliderInitialized === 'true') return;

      observer.observe(el);
    });
  },

  heroSlider(el: HTMLElement) {
    const loadSlideAssets = (slide: HTMLElement | null) => {
      if (!slide || slide.dataset.assetsLoaded === 'true') return;

      const picture = slide.querySelector('picture');
      if (!picture) return;

      const sources = picture.querySelectorAll('source[data-srcset]');
      const img = picture.querySelector(
        'img[data-src]',
      ) as HTMLImageElement | null;

      sources.forEach((source) => {
        const dataSrcset = source.getAttribute('data-srcset');

        if (!dataSrcset) return;

        source.setAttribute('srcset', dataSrcset);
        source.removeAttribute('data-srcset');
      });

      if (img) {
        const dataSrc = img.getAttribute('data-src');

        if (dataSrc) {
          img.setAttribute('src', dataSrc);
          img.removeAttribute('data-src');
        }
      }

      slide.dataset.assetsLoaded = 'true';
    };

    const slider = new Splide(el, {
      type: 'slide',
      rewind: true,
      perPage: 1,
      pagination: true,
      arrows: true,
    });

    slider.on('mounted move', () => {
      const activeSlide = el.querySelector(
        '.splide__slide.is-active',
      ) as HTMLElement | null;
      const prevSlide = el.querySelector(
        '.splide__slide.is-prev',
      ) as HTMLElement | null;
      const nextSlide = el.querySelector(
        '.splide__slide.is-next',
      ) as HTMLElement | null;

      loadSlideAssets(activeSlide);
      loadSlideAssets(prevSlide);
      loadSlideAssets(nextSlide);
    });

    slider.mount();
  },

  prodSlider(item: HTMLElement) {
    const slideCount = item.querySelectorAll('.splide__slide').length;

    new Splide(item, {
      type: 'slide',
      arrows: true,
      gap: 30,
      perPage: 5,
      pagination: slideCount >= 6,
      rewind: true,
      breakpoints: {
        1200: {
          perPage: 2,
          perMove: 1,
          padding: { right: '40%' },
          pagination: slideCount >= 3,
        },

        767: {
          perPage: 1,
          perMove: 1,
          padding: { right: '40%' },
          pagination: slideCount >= 2,
        },
      },
    }).mount();
  },
  itemSlider(item: HTMLElement) {
    const slideCount = item.querySelectorAll('.splide__slide').length;

    new Splide(item, {
      type: 'slide',
      arrows: true,
      perPage: 1,
      pagination: false,
      rewind: true,
      destroy: slideCount < 2,
    }).mount();
  },

  catsSlider(item: HTMLElement) {
    const slideCount = item.querySelectorAll('.splide__slide').length;

    new Splide(item, {
      type: 'slide',
      arrows: true,
      gap: 30,
      perPage: 6,
      pagination: slideCount >= 7,
      rewind: true,
      breakpoints: {
        1200: {
          perPage: 2,
          perMove: 1,
          padding: { right: '30%' },
          pagination: slideCount >= 3,
        },

        767: {
          perPage: 1,
          perMove: 1,
          padding: { right: '30%' },
          pagination: slideCount >= 2,
        },
      },
    }).mount();
  },

  textSlider(item: HTMLElement) {
    new Splide(item, {
      type: 'loop',
      autoWidth: true,
      pagination: false,
      arrows: false,
      gap: 25,
      drag: 'free',
      focus: 'center',
      padding: {
        right: '30%',
      },

      autoScroll: {
        speed: -1,
        pauseOnHover: false,
        pauseOnFocus: false,
      },

      breakpoints: {
        768: {
          gap: 25,
          padding: {
            right: '30%',
          },
        },

        500: {
          gap: 25,
          padding: {
            right: '30%',
          },
        },
      },
    }).mount({ AutoScroll });
  },

  setProdThumbnailsSlider(item: HTMLElement) {
    const productMainImages = document.getElementById('mainImage') as
      | HTMLElement
      | undefined;
    const thumbnails = document.getElementById('thumbnails') as
      | HTMLElement
      | undefined;

    if (productMainImages && thumbnails) {
      const slideCount =
        productMainImages.querySelectorAll('.splide__slide').length;

      const main = new Splide(productMainImages, {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: slideCount > 4,
        breakpoints: {
          1200: {
            arrows: false,
            pagination: true,
          },
        },
      });

      const thumbnailHeight = 100;
      const thumbnailGap = 5;
      const visibleThumbnails = Math.min(slideCount, 5);

      const thumbnailsNav = new Splide(thumbnails, {
        type: slideCount > 4 ? 'loop' : 'slide',
        direction: 'ttb',
        isNavigation: true,
        height: `${
          thumbnailHeight * visibleThumbnails +
          thumbnailGap * Math.max(visibleThumbnails - 1, 0)
        }px`,

        fixedHeight: `${thumbnailHeight}px`,
        gap: `${thumbnailGap}px`,

        perPage: 5,
        perMove: 1,

        pagination: false,
        arrows: false,
        trimSpace: true,

        breakpoints: {
          1200: {
            direction: 'ltr',
            height: 'auto',
            fixedHeight: 'auto',
            fixedWidth: '110px',
            pagination: false,
            gap: '15px',
            perPage: 3,
            perMove: 1,
            drag: slideCount > 3,
          },
        },
      });

      main.sync(thumbnailsNav);
      main.mount();
      thumbnailsNav.mount();
    }
  },

  setProdGallerySlider(item: HTMLElement) {
    const productMainImages = document.getElementById('mainImageGallery') as
      | HTMLElement
      | undefined;
    const thumbnails = document.getElementById('thumbnailsGallery') as
      | HTMLElement
      | undefined;

    if (productMainImages && thumbnails) {
      const slideCount =
        productMainImages.querySelectorAll('.splide__slide').length;

      const main = new Splide(productMainImages, {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: slideCount > 4,
      });

      const thumbnailsNav = new Splide(thumbnails, {
        type: slideCount > 4 ? 'loop' : 'slide',
        isNavigation: true,
        direction: 'ltr',
        height: '100%',
        gap: '15px',
        perPage: 3,
        perMove: 1,
        pagination: false,
        arrows: false,
      });

      main.sync(thumbnailsNav);
      main.mount();
      thumbnailsNav.mount();
    }
  },
};

export default sliders;
